/**
 * Loads every device row for a group by calling the GraphQL query
 * `GetGroupDevices` repeatedly until:
 *  - the API returns no `nextToken` (we got everything) or
 *  - we hit the caller's `maxPages` safety cap
 *
 * Used by group CSV export to fetch all matching devices across pages,
 * respecting the current search filter - same pattern as org export.
 */

import type { GetGroupDevicesQuery } from 'gqlHooks';
import type { ApolloError } from '@apollo/client';
import { CONSTANTS } from 'utils/constants';

// shape of a single device row returned by `GetGroupDevices`
type Device = GetGroupDevicesQuery['groupDevices']['items'][number];

// minimal type for fetch one page of devices from the group
export type FetchGroupDevicesPage = (options: {
  variables: {
    organizationId: string;
    groupToken: string;
    limit: number;
    nextToken: string | null;
    // server-side prefix search for IMEI/device name.
    // pass undefined when search bar is empty = "give me the full device list in the group"
    searchTerm?: string;
    // match DeviceListTable: direct group members only, not nested devices
    recursive: boolean;
  };
}) => Promise<{
  data?: GetGroupDevicesQuery;
  error?: ApolloError;
}>;

export type FetchAllGroupDevicesParams = {
  organizationId: string; // required
  groupToken: string; // required
  searchTerm?: string; // optional search filter - empty string should be converted to undefined
  recursive?: boolean; // default false - matches table.tsx group device view
  pageSize?: number; // default: 500
  maxPages?: number; // if we stop because we hit this cap but `nextToken` is still available, `truncated` = true
  fetchPage: FetchGroupDevicesPage; // function to fetch one page of devices
};

export type FetchAllGroupDevicesResult = {
  devices: Device[]; // all device rows accumulated across pages
  truncated: boolean; // true if we hit `maxPages` but `nextToken` is still available; UI should show a warning
  error: ApolloError | null;
};

export async function fetchAllGroupDevices({
  organizationId,
  groupToken,
  searchTerm,
  recursive = false,
  pageSize = CONSTANTS.DEVICE_PAGE_SIZE,
  maxPages = CONSTANTS.DEVICE_PAGE_MAX_PAGES,
  fetchPage,
}: FetchAllGroupDevicesParams): Promise<FetchAllGroupDevicesResult> {
  // early exit - nothing to fetch
  if (!organizationId || !groupToken) {
    return { devices: [], truncated: false, error: null };
  }

  // initialize accumulator and pagination state
  const accumulated: Device[] = [];
  let nextToken: string | null | undefined = null;
  let pagesFetched = 0;

  try {
    do {
      const { data, error: pageError } = await fetchPage({
        variables: {
          organizationId,
          groupToken,
          limit: pageSize,
          nextToken: nextToken ?? null,
          searchTerm: searchTerm || undefined, // undefined = full list
          recursive,
        },
      });
      if (pageError) throw pageError;

      const page = data?.groupDevices;
      if (page?.items?.length) accumulated.push(...page.items);
      nextToken = page?.nextToken ?? null;
      pagesFetched += 1;
    } while (nextToken && pagesFetched < maxPages);

    const truncated = Boolean(nextToken && pagesFetched >= maxPages);

    if (truncated) {
      // eslint-disable-next-line no-console
      console.warn('fetchAllGroupDevices: hit maxPages guard', {
        organizationId,
        groupToken,
        pagesFetched,
        loaded: accumulated.length,
        maxPages,
      });
    }

    return { devices: accumulated, truncated, error: null };
  } catch (e) {
    // export all or nothing
    return { devices: [], truncated: false, error: e as ApolloError };
  }
}
